import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import { useCreateComment } from "lib/hooks/api/comments/useCreateComment";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { CommentObjectType } from "lib/types/graphQLEnums";
import { updateBidQuery } from "../../../lib/hooks/api/bid/bids/updateBidCache";
import { serializeComment } from "../../comments/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bidId: string;
  bidName: string;
  stageName: string;
  onConfirm: () => void;
};

const commentSchema = z.object({
  comment: z.string().optional(),
});

type CommentForm = z.infer<typeof commentSchema>;

export default function NoBidModal({
  open,
  onOpenChange,
  bidId,
  bidName,
  stageName,
  onConfirm,
}: Props) {
  const { control, handleSubmit, reset } = useForm<CommentForm>({
    resolver: zodResolver(commentSchema),
    defaultValues: {},
  });

  const queryClient = useQueryClient();
  const currentUser = useAssertCurrentUser();

  const { mutate: createComment } = useCreateComment({
    onSuccess: async (data) => {
      const newComment = data.createComment;
      updateBidQuery(queryClient, bidId, (bid) => ({
        ...bid,
        comments: [serializeComment(newComment, currentUser), ...bid.comments],
      }));
    },
  });

  const onSubmit = (values: CommentForm) => {
    if (!bidId) return;
    if (values.comment) {
      createComment({
        request: {
          content: values.comment,
          objectId: bidId,
          objectType: CommentObjectType.Bid,
          objectName: bidName,
        },
      });
    }
    onConfirm();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange} maxWidth="560px" showCloseButton={false}>
      <Content onSubmit={handleSubmit(onSubmit)}>
        <Flex gap={8} alignItems="center">
          <UIcon icon="xCircle" size={20} />
          <Text h3>Marked as {stageName}</Text>
        </Flex>
        <DescriptionContainer>
          <Text>
            Keep your team in the loop by adding a brief note about why you've marked this as{" "}
            {stageName}.
          </Text>
        </DescriptionContainer>
        <TextAreaContainer>
          <TextArea
            rows={4}
            name="comment"
            label=""
            placeholder="Add optional comment"
            control={control}
            aria-label="Comment"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onPressEnter={(e) => {
              if (e.shiftKey) return;

              e.preventDefault();
              e.stopPropagation();
              return handleSubmit(onSubmit)();
            }}
          />
        </TextAreaContainer>
        <ButtonContainer>
          <Button type="reset" buttonType="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit">Confirm {stageName}</Button>
        </ButtonContainer>
      </Content>
    </Modal>
  );
}

const Content = styled.form({
  padding: 16,
});

const DescriptionContainer = styled.div({
  marginTop: 8,
});

const TextAreaContainer = styled.div({
  marginTop: 16,
});

const ButtonContainer = styled(Flex)({
  marginTop: 24,
  justifyContent: "flex-end",
  gap: 8,
});
